import { useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { allArticles } from "@/mocks/articles";

export default function ArtigoPage() {
  const { slug } = useParams<{ slug: string }>();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [slug]);

  const article = allArticles.find((a) => a.slug === slug);

  if (!article) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-24 pb-10">
          <div className="max-w-3xl mx-auto px-4 md:px-6 text-center">
            <h1 className="font-heading text-2xl font-bold text-foreground-950 mb-4">Artigo não encontrado</h1>
            <p className="text-foreground-500 mb-6">O artigo que você procura não está disponível.</p>
            <Link to="/" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-primary-500 text-white text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap">
              <i className="ri-arrow-left-line w-4 h-4 flex items-center justify-center"></i>
              Voltar ao Início
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />

      <main className="pt-20 pb-10">
        <article className="max-w-3xl mx-auto px-4 md:px-6">
          {/* Breadcrumbs */}
          <nav className="mb-8 flex items-center gap-2 text-xs text-foreground-400 flex-wrap" aria-label="Breadcrumb">
            <Link to="/" className="hover:text-primary-500 transition-colors">Home</Link>
            <i className="ri-arrow-right-s-line w-3 h-3 flex items-center justify-center"></i>
            <span className="text-foreground-600 font-medium">{article.category}</span>
            <i className="ri-arrow-right-s-line w-3 h-3 flex items-center justify-center"></i>
            <span className="text-foreground-500">{article.title.slice(0, 60)}...</span>
          </nav>

          {/* Article Header */}
          <header className="mb-8">
            {/* Featured Image */}
            <div className="rounded-xl overflow-hidden mb-8 aspect-[2/1]">
              <img
                src={article.image}
                alt={article.title}
                title={article.title}
                className="w-full h-full object-cover"
              />
            </div>
            <h1 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950 leading-tight mb-4">
              {article.title}
            </h1>
            <div className="flex flex-wrap items-center gap-4 text-xs text-foreground-400">
              <span>Atualizado em: {new Date(article.date).toLocaleDateString("pt-BR")}</span>
              <span className="w-1 h-1 rounded-full bg-foreground-300"></span>
              <span>{article.readTime} de leitura</span>
              <span className="w-1 h-1 rounded-full bg-foreground-300"></span>
              <span>Por Equipe Hoobuy Spreadsheet</span>
            </div>
          </header>

          {/* Article Content */}
          {article.sections.map((section, idx) => {
            if (section.type === "heading") {
              return (
                <section key={idx} className="mb-8">
                  <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-4">
                    {section.heading || section.content}
                  </h2>
                </section>
              );
            }

            if (section.type === "paragraph") {
              return (
                <section key={idx} className="mb-6">
                  <p
                    className="text-foreground-700 leading-relaxed mb-4"
                    dangerouslySetInnerHTML={{ __html: section.content || "" }}
                  />
                </section>
              );
            }

            if (section.type === "list" && section.items) {
              return (
                <section key={idx} className="mb-8">
                  <ul className="space-y-3">
                    {section.items.map((item, i) => (
                      <li key={i} className="flex gap-3 p-4 rounded-xl bg-background-100 border border-background-200/50">
                        <div className="w-6 h-6 flex items-center justify-center rounded-full bg-accent-100 text-accent-600 flex-shrink-0 mt-0.5">
                          <i className="ri-check-line text-xs"></i>
                        </div>
                        <div>
                          <h3 className="text-sm font-semibold text-foreground-900 mb-1">{item.name}</h3>
                          <p className="text-xs text-foreground-500 leading-relaxed">{item.desc}</p>
                        </div>
                      </li>
                    ))}
                  </ul>
                </section>
              );
            }

            if (section.type === "steps" && section.steps) {
              return (
                <section key={idx} className="mb-8">
                  <div className="space-y-5">
                    {section.steps.map((s, i) => (
                      <div key={i} className="flex gap-4">
                        <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 font-bold text-sm flex-shrink-0">
                          {s.step}
                        </div>
                        <div>
                          <h3 className="text-sm font-semibold text-foreground-900 mb-1">{s.title}</h3>
                          <p className="text-sm text-foreground-600 leading-relaxed">{s.text}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </section>
              );
            }

            if (section.type === "tips" && section.tips) {
              return (
                <section key={idx} className="mb-8">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {section.tips.map((tip, i) => (
                      <div key={i} className="p-4 rounded-xl bg-background-100 border border-background-200/50">
                        <div className="w-9 h-9 flex items-center justify-center rounded-full bg-accent-100 text-accent-600 mb-3">
                          <i className={`${tip.icon} text-sm`}></i>
                        </div>
                        <h3 className="text-sm font-semibold text-foreground-900 mb-1">{tip.title}</h3>
                        <p className="text-xs text-foreground-500 leading-relaxed">{tip.text}</p>
                      </div>
                    ))}
                  </div>
                </section>
              );
            }

            if (section.type === "cta") {
              return (
                <section key={idx} className="mb-8">
                  <a
                    href={section.ctaUrl || "https://kaspreadsheet.com"}
                    target="_blank"
                    rel="noopener nofollow"
                    className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-primary-500 text-white text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    {section.ctaText || "Acessar Hoobuy Sheet"}
                    <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center"></i>
                  </a>
                </section>
              );
            }

            if (section.type === "image" && section.imageUrl) {
              return (
                <section key={idx} className="mb-8">
                  <div className="rounded-xl overflow-hidden aspect-[16/9]">
                    <img
                      src={section.imageUrl}
                      alt={section.heading || ""}
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                  </div>
                </section>
              );
            }

            return null;
          })}

          {/* Conclusion CTA */}
          <section className="mb-10 p-6 md:p-8 rounded-2xl bg-primary-50 border border-primary-200/60 text-center">
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-3">
              Comece Agora com a Hoobuy Spreadsheet
            </h2>
            <p className="text-foreground-600 text-sm mb-6 max-w-md mx-auto leading-relaxed">
              Não perca mais tempo procurando produtos em dezenas de sites. Acesse nossa Hoobuy Spreadsheet e encontre tudo em um só lugar.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="w-full sm:w-auto px-6 py-3 rounded-full bg-primary-500 text-white font-semibold text-sm hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                Hoobuy Sheet
              </a>
              <Link
                to="/"
                className="w-full sm:w-auto px-6 py-3 rounded-full border border-primary-300 text-primary-700 font-medium text-sm hover:bg-primary-100 transition-colors cursor-pointer whitespace-nowrap"
              >
                Voltar ao Início
              </Link>
            </div>
          </section>

          {/* Author Box */}
          <div className="p-5 rounded-xl bg-background-100 border border-background-200/50 flex gap-4 items-start">
            <div className="w-12 h-12 rounded-full bg-gradient-to-br from-primary-400 to-accent-400 flex items-center justify-center text-white font-bold flex-shrink-0">
              HS
            </div>
            <div>
              <p className="text-sm font-semibold text-foreground-900">Equipe Hoobuy Spreadsheet</p>
              <p className="text-xs text-foreground-500 leading-relaxed mt-1">
                Especialistas em compras internacionais, agentes chineses e spreadsheet Hoobuy. Atualizamos nosso conteúdo semanalmente para garantir as informações mais precisas e úteis para você.
              </p>
            </div>
          </div>

          {/* Related Articles */}
          <section className="mt-12 pt-8 border-t border-background-200/60">
            <h3 className="font-heading text-lg font-bold text-foreground-950 mb-5">Artigos Relacionados</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {allArticles
                .filter((a) => a.slug !== article.slug)
                .slice(0, 4)
                .map((a) => (
                  <Link
                    key={a.slug}
                    to={`/artigo/${a.slug}`}
                    className="flex gap-3 p-3 rounded-xl bg-background-100 border border-background-200/50 hover:border-primary-200 transition-colors cursor-pointer group"
                  >
                    <div className="w-20 h-16 rounded-lg overflow-hidden flex-shrink-0">
                      <img
                        src={a.image}
                        alt={a.title}
                        className="w-full h-full object-cover"
                        loading="lazy"
                      />
                    </div>
                    <div className="min-w-0">
                      <span className="text-xs text-primary-500 font-medium">{a.category}</span>
                      <p className="text-sm font-medium text-foreground-900 group-hover:text-primary-600 transition-colors line-clamp-2 mt-1">
                        {a.title}
                      </p>
                    </div>
                  </Link>
                ))}
            </div>
          </section>

          {/* Article Schema */}
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{
              __html: JSON.stringify({
                "@context": "https://schema.org",
                "@type": "Article",
                headline: article.title,
                description: article.description,
                author: { "@type": "Organization", name: "Equipe Hoobuy Spreadsheet" },
                datePublished: article.date,
                dateModified: article.date,
                publisher: { "@type": "Organization", name: "Hoobuy Spreadsheet" },
                image: article.image,
              }),
            }}
          />
        </article>
      </main>

      <Footer />
    </div>
  );
}